import React, { useEffect, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import SettingsModal from '../../Settings/SettingsModal';
import { addToQueue, apiError, fetchSkatteverketSuggestion } from './paymentsApi';
import { ROW_STATUS, kr } from './paymentStatus';

/**
 * Skattekonto page → "Lägg i betalningskö" (plan UC3): payee Skatteverket
 * (bankgiro 5050-1055), OCR read-only from the latest Skatteverket sync,
 * amount prefilled from what is due and editable. Refused by the server
 * without a completed sync — the OCR is never computed.
 */
const SkatteverketQueueDialog = ({ show, companyId, onClose, onQueued }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show) return;
    setLoading(true);
    setError(null);
    fetchSkatteverketSuggestion(companyId)
      .then((s) => {
        setInfo(s);
        const due = s.due?.amountDue;
        setAmount(due && due > 0 ? Number(due).toFixed(2) : '');
        // Never default to a date in the past: the earliest open due date, or the next weekday.
        const next = new Date();
        while ([0, 6].includes(next.getDay())) next.setDate(next.getDate() + 1);
        const nextWeekday = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
        const nextDue = s.due?.nextDueDate;
        setDate(nextDue && nextDue >= nextWeekday ? nextDue : nextWeekday);
      })
      .catch((err) => setError(apiError(err, 'Kunde inte hämta uppgifter om skattekontot.').message))
      .finally(() => setLoading(false));
  }, [show, companyId]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await addToQueue(companyId, { sourceType: 'skatteverket', amount, requested_date: date || null });
      toast.success('Skattebetalningen lades i betalningskön.');
      onQueued(result);
    } catch (err) {
      setError(apiError(err, 'Kunde inte lägga betalningen i kön.').message);
    } finally {
      setBusy(false);
    }
  };

  const footer = (
    <>
      <button type="button" className="ll-btn ll-btn--secondary" onClick={onClose} disabled={busy}>Avbryt</button>
      <button type="button" className="ll-btn ll-btn--primary" onClick={submit} disabled={busy || loading || !info?.ocr || !(Number(amount) > 0)}>{busy ? 'Lägger i kön…' : 'Lägg i betalningskö'}</button>
    </>
  );
  const label = { fontSize: 13, fontWeight: 600 };
  return (
    <SettingsModal show={show} onHide={onClose} title="Betala till skattekontot" footer={footer}>
      {loading ? <div className="text-center py-3"><Spinner animation="border" size="sm" /></div> : (
        <>
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 10 }}>
            Mottagare <strong>Skatteverket</strong>, bankgiro <strong>5050-1055</strong>.
            {info?.ocr ? (
              <> OCR <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{info.ocr}</strong> från hämtningen {info.ocrFetchedAt ? String(info.ocrFetchedAt).slice(0, 10) : ''}.</>
            ) : (
              <span className="text-danger"> Hämta skattekontot från Skatteverket först — OCR-numret kommer därifrån och räknas aldrig fram.</span>
            )}
          </div>
          {info?.due && (
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
              Att betala enligt deklarationerna: <strong>{info.due.amountDue != null ? kr(info.due.amountDue) : 'okänt'}</strong>{info.due.nextDueDate ? `, senast ${info.due.nextDueDate}` : ''}.
              {info.due.items?.length > 0 && (
                <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                  {info.due.items.map((i, idx) => <li key={idx}>{i.label || i.kind} · {i.dueDate} · {i.amount != null ? kr(i.amount) : 'okänt belopp'}</li>)}
                </ul>
              )}
            </div>
          )}
          {info?.live?.length > 0 && (
            <div style={{ fontSize: 12, color: '#b45309', marginBottom: 10 }}>
              Redan i betalningskön eller i en fil: {info.live.map((l) => `${kr(l.amount)} (${ROW_STATUS[l.status]?.label || l.status}, ${l.requested_date})`).join(', ')}. <Link to={`/${companyId}/payments`}>Visa betalningar</Link>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Form.Group><Form.Label style={label}>Belopp (SEK)</Form.Label><Form.Control type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} /></Form.Group>
            <Form.Group><Form.Label style={label}>Betalas senast</Form.Label><Form.Control type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Form.Group>
          </div>
          {error && <div className="text-danger" style={{ fontSize: 13, marginTop: 8 }}>{error}</div>}
        </>
      )}
    </SettingsModal>
  );
};

export default SkatteverketQueueDialog;
